/* eslint-disable @typescript-eslint/no-floating-promises */
import React, { memo, useRef } from 'react'
import Image from 'next/image'
import StyledComponentsRegistry from '@configs/registry'
import { pageEvent, waitForAnalyticsLoad } from '@acko-tech/building-blocks.ui.acko-analytics'
import { SEGMENT } from '@components/MainWidget/common/widgetSegments'
import { onDownloadApp } from '@components/MainWidget/common/helper'
import { PrimaryButton } from '@acko-tech/building-blocks.ui.button'
import { widgetJourney, fetchWidgetProductFn } from '@configs/segmentEvents.config'
import { fastagBalanceContainerData } from '@helpers/fastagBalanceHelper'
import PropertyControlledComponent from '@components/PropertyControlledComponent/PropertyControlledComponent'
import { formatVehicleRegNumber } from '@utils/regNoFormatter'
import { isFastagLandingWidget, isHsrpLandingWidget } from '@helpers/vasLandingPageHelper'
import styles from './mobileChallanModal.module.scss'
import {
  ACKO_LOGO_PURPLE,
  CHALLAN_NOTIFICATION_HEADING,
  CHALLAN_NOTIFICATION_SUBHEADING,
  INDIA_LOGO,
  defaultVehicleImage,
  downloadButtonText,
  newChallanMobileModal,
} from './constants'
import { vehicleTypeConfig } from '../common/commonWidgetConfig'
import { LOW_BALANCE_TEXT, SUFFICIENT_BALANCE_TEXT } from '../FastagWidget/constants'
import { DASH_FALLBACK } from '../common/commonConstants'

interface MobileChallanModalProps {
  onClose: () => void
  vasWrapperProps: any
  vehicleInfoData?: any
  segmentEventCommonProperties?: any
  newVasLandingFlow?: boolean
  widgetType: string
}

const MobileChallanModal: React.FC<MobileChallanModalProps> = memo(
  ({
    onClose,
    vasWrapperProps,
    vehicleInfoData,
    segmentEventCommonProperties,
    newVasLandingFlow = false,
    widgetType,
  }) => {
    const hasFiredRef = useRef(false)
    const isFastagLandingPage = isFastagLandingWidget(widgetType)
    const isHsrpLandingPage = isHsrpLandingWidget(widgetType)
    const topSectionClassNames = `${styles.topSection} ${isFastagLandingPage ? styles.fastagStyles : ''} ${
      isHsrpLandingPage ? styles.hsrpStyles : ''
    }`

    React.useEffect(() => {
      if (newVasLandingFlow && !hasFiredRef.current) {
        waitForAnalyticsLoad().then(() => {
          pageEvent(SEGMENT.VIEW_PAGE, {
            ...segmentEventCommonProperties,
            product: fetchWidgetProductFn[widgetType]?.(),
            phone: vehicleInfoData?.mobile_number || '',
            registration_number: vehicleInfoData?.reg_number || '',
            vehicle_type: vehicleTypeConfig?.[vehicleInfoData?.vehicleInfo?.vehicle_type_v2] || 'N/A',
            mm_name: vehicleInfoData?.vehicleInfo?.model_name || '',
            previous_policy_expiry_date: vehicleInfoData?.vehicleInfo?.previous_policy_expiry_detail || '',
            expiry_date: vehicleInfoData?.vehicleInfo?.previous_policy_expiry_detail || '',
            vas_amount: null,
            journey: widgetJourney[widgetType],
          })
          hasFiredRef.current = true
        })
      }
    }, [newVasLandingFlow])

    const { hasBalanceContainer, balanceValue, isBalanceLow } = fastagBalanceContainerData(vehicleInfoData)

    return (
      <div>
        <div className={`${styles.overlay} ${isFastagLandingPage ? styles.darkBackground : ''}`}>
          <div className={styles.modal}>
            <button type="button" className={styles.closeButton} onClick={onClose}>
              &times;
            </button>
            <div>
              <PropertyControlledComponent controllerProperty={hasBalanceContainer} fallback={null}>
                <div className={styles.balanceBanner}>
                  <Image
                    width={140}
                    height={80}
                    className={styles.media}
                    alt="Car Balance image"
                    src={vehicleInfoData?.vehicleImage || defaultVehicleImage?.Default}
                  />
                  <div className={styles.balanceContent}>
                    <div className={`${styles.balanceStatus} ${isBalanceLow ? styles.lowBalance : ''}`}>
                      {isBalanceLow ? LOW_BALANCE_TEXT : SUFFICIENT_BALANCE_TEXT}
                    </div>
                    <h3 className={styles.balance}>₹{Number.isFinite(balanceValue) ? balanceValue : DASH_FALLBACK}</h3>
                  </div>
                </div>
              </PropertyControlledComponent>
              <div className={topSectionClassNames}>
                <div className={styles.headingSection}>
                  <h1 className={styles.heading}>
                    {vasWrapperProps?.challanMobileHeading || newChallanMobileModal?.heading}
                  </h1>
                  {hasBalanceContainer && <div className={styles.line} />}
                  <span className={styles.subHeading}>
                    {vasWrapperProps?.challanMobileDescription || newChallanMobileModal?.subHeading}
                  </span>
                </div>
                <PropertyControlledComponent controllerProperty={!isHsrpLandingPage} fallback={null}>
                  <div className={styles.cards}>
                    {(vasWrapperProps?.challanKeyFeaturesMobile || newChallanMobileModal?.cards)?.map(
                      (cardItem, index) => (
                        <div className={styles.cardContent} key={index}>
                          <Image
                            src={cardItem?.src || cardItem?.icon}
                            alt={cardItem?.title || cardItem?.alt}
                            width={20}
                            height={20}
                            className={styles.cardImage}
                          />
                          <span className={styles.cardText}>{cardItem?.title || cardItem?.text}</span>
                        </div>
                      )
                    )}
                  </div>
                </PropertyControlledComponent>
              </div>
            </div>
            <PropertyControlledComponent
              controllerProperty={!isFastagLandingPage && !isHsrpLandingPage}
              fallback={null}>
              <div className={styles.bottomSection}>
                <div className={styles.phoneMockup}>
                  <Image
                    src={newChallanMobileModal.mobileHomeScreen}
                    alt="Challan Info"
                    className={styles.phoneImageOverlay}
                    width={340}
                    height={600}
                    loading="lazy"
                  />
                  <div className={styles.vehicleName}>{`${vehicleInfoData?.vehicleInfo?.db_make_name || ''} ${
                    vehicleInfoData?.vehicleInfo?.db_model_name || ''
                  }`}</div>
                  <PropertyControlledComponent
                    controllerProperty={!!vehicleInfoData?.vehicleInfo?.registration_number}
                    fallback={null}>
                    <div className={styles.numberPlate}>
                      <div className={styles.indiaIcon}>
                        <Image src={INDIA_LOGO} alt="INDIA logo" width={12} height={15} loading="lazy" />
                      </div>
                      <div className={styles.formatedRegNo}>
                        {formatVehicleRegNumber(vehicleInfoData?.vehicleInfo?.registration_number)}
                      </div>
                    </div>
                  </PropertyControlledComponent>
                  <Image
                    src={
                      vehicleInfoData?.vehicleImage ||
                      defaultVehicleImage?.[vehicleInfoData?.vehicleInfo?.vehicle_type_v2] ||
                      defaultVehicleImage?.Default
                    }
                    alt="Challan Vehicle Image"
                    className={styles.vehicleImage}
                    width={100}
                    height={60}
                    loading="lazy"
                  />
                  <div className={styles.notification}>
                    <Image
                      src={ACKO_LOGO_PURPLE}
                      alt="Challan Info"
                      className={styles.logoImage}
                      width={40}
                      height={40}
                      loading="lazy"
                    />
                    <div className={styles.notificationInfo}>
                      <div className={styles.notificationHeading}>{CHALLAN_NOTIFICATION_HEADING}</div>
                      <div className={styles.notificationSubHeading}>{CHALLAN_NOTIFICATION_SUBHEADING}</div>
                    </div>
                  </div>
                </div>
              </div>
            </PropertyControlledComponent>
            <PropertyControlledComponent controllerProperty={isHsrpLandingPage} fallback={null}>
              <div className={styles.hsrpBottomSection}>
                <Image
                  src={vasWrapperProps?.carImageSrc}
                  alt="Hsrp RC Card"
                  className={styles.rcCard}
                  width={310}
                  height={350}
                  loading="lazy"
                />
              </div>
            </PropertyControlledComponent>
          </div>
        </div>
        <div className={styles.bottomFloaterContainer}>
          <StyledComponentsRegistry>
            <PrimaryButton
              className={styles.downloadButton}
              onClick={() => {
                onDownloadApp({
                  regNo: vehicleInfoData?.reg_number,
                  widgetType,
                  phone: vehicleInfoData?.mobile_number,
                  vehicleData: vehicleInfoData?.vehicleInfo,
                  providerData: vehicleInfoData?.selectedProvider,
                })
              }}>
              {downloadButtonText?.[widgetType] || downloadButtonText?.Default}
            </PrimaryButton>
          </StyledComponentsRegistry>
        </div>
      </div>
    )
  }
)

export default MobileChallanModal
